<template>
  <modal :open="internalOpen" :width="900">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.driver.payoutList.bulkEditPayoutModal.title') }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <Multiselect
        v-model="rawStatus"
        :error="errors?.status"
        :options="getStatus"
        :label="$t('payout.status')"
        class="w-full mb-5"
        :can-deselect="false"
        :can-clear="false"
      />
      <span>{{ computedPayoutStatus }}</span>
      <div class="w-full text-center">
        <custom-button
          variant="primary"
          class="mt-15"
          :busy="loading"
          @click="save"
        >
          {{ $t('save') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { enumOptionsProvider } from '@utils/table/hooks/provider';
import { ref } from 'vue';
import { type CampaignGroup, type Payout, PayoutStatus } from '@/entities/Entities';

interface Props {
  open: boolean;
  payoutIds: string[];
  campaignGroup: CampaignGroup;
  payouts: Payout[];
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const { t } = useI18n();

const notify = useNotify();

const rawStatus = ref<string | undefined>(undefined);

const {
  data: payoutStatus,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  string().nullable().required(t('validation.notEmpty')),
  rawStatus,
);

// only allow correct state to be selected in dropdown based on selected payouts
async function getStatus() {
  let status = await enumOptionsProvider('/enums/PayoutStatus', (v) => ({
    label: t(`payoutStatus.${v}`),
    value: v,
  }))();

  if (props.payouts.filter((p) => props.payoutIds.includes(p.id)).some((p) => [PayoutStatus.PENDING.toString()].includes(p.status))) {
    status = status.filter((s) => s.value !== PayoutStatus.PAYED.toString());
  }

  return status;
}

function resetData() {
  setValidation(false);
  rawStatus.value = undefined;
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    await exec(`${props.campaignGroup?.['@id']}/payouts-status-change`, {
      method: 'PUT',
      body: {
        payoutIds: props.payoutIds,
        status: payoutStatus.value,
      },
    });
    notify?.({
      variant: 'success',
      title: t('pages.driver.payoutList.bulkEditPayoutModal.saveSuccess'),
    });
    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.payoutList.bulkEditPayoutModal.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
